import { FC } from 'react';
import { AutoComplete, Button, Space, Spin, Typography } from 'antd';
import { DownOutlined, ReloadOutlined } from '@ant-design/icons';
import FlexBox from '../../../../../components/atoms/FlexBox';
import FlexCol from '../../../../../components/atoms/FlexCol';
import { DEFAULT_SIGNIFY_CUSTOMER_ID } from '#types/SignifyApi.types';

const { Text } = Typography;

/**
 * Props for SignifyDistributorSelector component
 */
interface SignifyDistributorSelectorProps {
  selectedDistributor: string;
  distributorSearchValue: string;
  loading: boolean;
  loadingDistributors: boolean;
  filteredDistributors: Array<{
    uid: string;
    name: string;
    town?: string;
    uidForDisplay?: string;
  }>;
  filteredDistributorsCount: number;
  totalDistributors: number;
  distributorSearchTerm: string;
  onDistributorSearch: (value: string) => void;
  onDistributorSelect: (value: string) => void;
  onDistributorClear: () => void;
  onRefreshDistributors: () => void;
}

/**
 * Component for searching and selecting Signify distributors
 * Provides autocomplete functionality with refresh capability
 */
const SignifyDistributorSelector: FC<SignifyDistributorSelectorProps> = ({
  distributorSearchValue,
  loading,
  loadingDistributors,
  filteredDistributors,
  filteredDistributorsCount,
  totalDistributors,
  distributorSearchTerm,
  onDistributorSearch,
  onDistributorSelect,
  onDistributorClear,
  onRefreshDistributors,
}) => {
  return (
    <FlexCol gap={0}>
      <FlexBox justifyStart gap={10} style={{ width: '100%' }}>
        {filteredDistributorsCount < totalDistributors && (
          <Text type="secondary">
            <small>
              Showing {filteredDistributorsCount} of {totalDistributors}
            </small>
          </Text>
        )}
      </FlexBox>
      <FlexBox gap={0} style={{ width: '100%' }}>
        <Space.Compact style={{ width: '100%' }}>
          <AutoComplete
            aria-label="Select distributor"
            style={{ width: '100%' }}
            styles={{ popup: { root: { width: 400 } } }}
            value={distributorSearchValue}
            options={filteredDistributors.map((distributor) => ({
              value: distributor.uid,
              label: (
                <div>
                  <div>
                    {distributor.name}
                    {distributor.uid === DEFAULT_SIGNIFY_CUSTOMER_ID && ' (Default)'}
                  </div>
                  <Text type="secondary">
                    <small>
                      {distributor.town && `${distributor.town} | `}
                      ID: {distributor.uidForDisplay || distributor.uid}
                    </small>
                  </Text>
                </div>
              ),
            }))}
            showSearch={{ onSearch: onDistributorSearch }}
            onSelect={onDistributorSelect}
            onChange={(value) => {
              if (!value) {
                onDistributorClear();
              }
            }}
            disabled={loading || loadingDistributors}
            placeholder={
              loadingDistributors
                ? 'Loading distributors...'
                : 'Search distributors (defaults to first option)'
            }
            allowClear
            suffix={<DownOutlined />}
            notFoundContent={
              loadingDistributors ? (
                <Spin size="small" />
              ) : distributorSearchTerm &&
                filteredDistributors.length === 0 ? (
                <Text type="secondary">No distributors found</Text>
              ) : null
            }
          />
          <Button
            onClick={onRefreshDistributors}
            aria-label="Refresh Distributors"
            icon={<ReloadOutlined />}
            loading={loadingDistributors}
          />
        </Space.Compact>
      </FlexBox>
    </FlexCol>
  );
};

export default SignifyDistributorSelector;